"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildMermaidInitializeConfig } from "@/src/lib/markdown/mermaid-config";
import { detectPdfBrowserEngine } from "@/src/lib/pdf/browser-engine";
import {
  buildPreviewRevision,
  buildPreviewShellKey,
  buildPrintDocumentCss,
  buildPrintDocumentHtml,
  defaultPreviewSurfaceBackground,
  getPdfPageAspectRatio,
  getPdfPagePreviewHeight,
  getPdfPagePreviewWidth,
  type PdfOptions,
} from "@/src/lib/pdf/print-document";
import { buildFontsCssHref, getFontStack } from "@/src/lib/pdf/fonts";
import type { DocumentDirection } from "@/src/lib/pdf/document-direction";
import type { ScriptFallbackKey } from "@/src/lib/pdf/script-fallbacks";
import {
  findSamplePreviewId,
  getSamplePreviewSnapshotMeta,
  loadSamplePreviewSnapshot,
  shouldUseSamplePreviewSnapshot,
  type SamplePreviewEngineSnapshot,
} from "@/src/lib/pdf/sample-preview-snapshot";
import { documentTitle, sampleMarkdown } from "../editor/constants";
import type { WorkspaceTab } from "../editor/types";
import { classNames } from "../ui/class-names";
import { paneVisibility } from "../layout/pane-visibility";
import {
  EmptyPreview,
  PreviewError,
  RenderingPreview,
} from "./preview-status";
import type { PreviewState } from "./types";
import {
  usePagedPreviewIframe,
  type PagedPreviewRequest,
} from "./use-paged-preview-iframe";
import { usePreviewPageScale } from "./use-preview-page-scale";

export function PreviewPane(
  props: Readonly<{
    activeTab: WorkspaceTab;
    /** When false, skip iframe/srcDoc work (mobile before Preview opt-in). */
    previewEnabled?: boolean;
    /**
     * When false, pause new pagination (mobile Editor tab). A settled preview
     * stays mounted while the document revision is unchanged; content changes
     * while hidden tear the cache down so background pagination cannot stick
     * the status spinner.
     */
    previewVisible?: boolean;
    pdfOptions: PdfOptions;
    previewState: PreviewState;
    scriptFallbacks?: readonly ScriptFallbackKey[];
    dir?: DocumentDirection;
    lang?: string;
    scrollContainerRef: React.RefObject<HTMLDivElement | null>;
    onClosePopovers?: () => void;
    onPageCountChange?: (pageCount: number) => void;
    onUpdatingChange?: (isUpdating: boolean) => void;
    /** Current editor markdown — used to gate the sample snapshot. */
    markdown?: string;
    /** Seed markdown for this embed — snapshot only while unchanged. */
    sampleMarkdown?: string;
    /** Embedded converter: prefer build-time sample snapshot when eligible. */
    preferSampleSnapshot?: boolean;
  }>,
) {
  const {
    activeTab,
    previewEnabled = true,
    previewVisible = true,
    pdfOptions,
    previewState,
    scriptFallbacks = [],
    dir = "ltr",
    lang = "en",
    scrollContainerRef,
    onClosePopovers,
    onPageCountChange,
    onUpdatingChange,
    markdown = "",
    sampleMarkdown: seedMarkdown = sampleMarkdown,
    preferSampleSnapshot = false,
  } = props;
  const renderedPreview = previewState.rendered;
  const hasRenderedPreview = renderedPreview !== null;
  const browserEngine = useMemo(() => detectPdfBrowserEngine(), []);
  const sampleSnapshotEligible = shouldUseSamplePreviewSnapshot({
    preferSampleSnapshot,
    markdown,
    sampleMarkdown: seedMarkdown,
    options: pdfOptions,
  });
  const sampleSnapshotMeta = sampleSnapshotEligible
    ? getSamplePreviewSnapshotMeta(browserEngine, markdown)
    : null;
  const sampleSnapshotLoadKey = sampleSnapshotEligible
    ? `${browserEngine}\u0001${markdown}`
    : null;
  const [loadedSampleSnapshot, setLoadedSampleSnapshot] = useState<{
    key: string;
    snapshot: SamplePreviewEngineSnapshot;
  } | null>(null);
  const sampleSnapshot =
    sampleSnapshotLoadKey &&
    loadedSampleSnapshot?.key === sampleSnapshotLoadKey
      ? loadedSampleSnapshot.snapshot
      : null;

  useEffect(() => {
    if (!sampleSnapshotLoadKey) {
      return;
    }

    let cancelled = false;
    void loadSamplePreviewSnapshot(browserEngine, markdown).then((snap) => {
      if (!cancelled && snap) {
        setLoadedSampleSnapshot({
          key: sampleSnapshotLoadKey,
          snapshot: snap,
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [browserEngine, markdown, sampleSnapshotLoadKey]);

  const isInitialRendering =
    previewVisible &&
    previewEnabled &&
    !sampleSnapshot &&
    (sampleSnapshotEligible ||
      (previewState.status === "rendering" && !hasRenderedPreview));
  const isMarkdownUpdating =
    previewVisible &&
    previewEnabled &&
    previewState.status === "rendering" &&
    hasRenderedPreview;
  const [previewSurfaceBackground, setPreviewSurfaceBackground] = useState(
    defaultPreviewSurfaceBackground,
  );
  const [frozenRequest, setFrozenRequest] =
    useState<PagedPreviewRequest | null>(null);
  const onClosePopoversRef = useRef(onClosePopovers);
  useEffect(() => {
    onClosePopoversRef.current = onClosePopovers;
  }, [onClosePopovers]);
  const previewPaneRef = useRef<HTMLElement | null>(null);
  const setPreviewPaneRef = useCallback((node: HTMLElement | null) => {
    previewPaneRef.current = node;

    if (!node || typeof window === "undefined") {
      return;
    }

    setPreviewSurfaceBackground(
      window.getComputedStyle(node).backgroundColor ||
        defaultPreviewSurfaceBackground,
    );
  }, []);

  // Build whenever preview is opted in — visibility only gates which request
  // reaches the iframe hook (live vs frozen cache vs tear-down).
  const liveRequest = useMemo((): PagedPreviewRequest | null => {
    if (!previewEnabled) {
      return null;
    }

    if (sampleSnapshot) {
      return {
        shellKey: `sample-snapshot:${findSamplePreviewId(markdown) ?? "unknown"}:${browserEngine}`,
        shellSrcDoc: sampleSnapshot.html,
        revision: sampleSnapshot.revision,
        bodyHtml: "",
        printCss: "",
        dir: dir === "rtl" ? "rtl" : "ltr",
        lang,
        previewSurfaceBackground,
        mermaidConfig: {},
      };
    }

    if (!renderedPreview) {
      return null;
    }

    const externalFontHref = buildFontsCssHref(pdfOptions.fontFamily, {
      scripts: scriptFallbacks,
      diagramFontFamily: pdfOptions.diagramFontFamily,
      codeFontFamily: pdfOptions.codeFontFamily,
    });
    const shellSrcDoc = buildPrintDocumentHtml({
      title: documentTitle,
      bodyHtml: renderedPreview.html,
      options: pdfOptions,
      variant: "preview",
      externalFontHref,
      scriptFallbacks,
      dir,
      lang,
      previewSurfaceBackground,
    });
    const revision = buildPreviewRevision(
      renderedPreview.html,
      pdfOptions,
      externalFontHref,
      previewSurfaceBackground,
      scriptFallbacks,
      dir,
      lang,
    );
    const printCss = buildPrintDocumentCss(
      pdfOptions,
      previewSurfaceBackground,
      {
        includePagedPageNumbers: pdfOptions.pageNumbers,
        includePreviewSheetChrome: true,
        scriptFallbacks,
      },
    );
    const mermaidConfig = buildMermaidInitializeConfig(
      getFontStack(pdfOptions.diagramFontFamily, scriptFallbacks),
      pdfOptions.diagramFontSize,
      pdfOptions.theme,
      pdfOptions.diagramTheme,
    );

    return {
      shellKey: buildPreviewShellKey(
        pdfOptions.fontFamily,
        scriptFallbacks,
        externalFontHref,
      ),
      shellSrcDoc,
      revision,
      bodyHtml: renderedPreview.html,
      printCss,
      dir: dir === "rtl" ? "rtl" : "ltr",
      lang,
      previewSurfaceBackground,
      mermaidConfig: mermaidConfig as Record<string, unknown>,
    };
  }, [
    browserEngine,
    dir,
    lang,
    markdown,
    pdfOptions,
    previewEnabled,
    previewSurfaceBackground,
    renderedPreview,
    sampleSnapshot,
    scriptFallbacks,
  ]);

  const previewRequest = useMemo((): PagedPreviewRequest | null => {
    if (!previewEnabled) {
      return null;
    }

    if (previewVisible) {
      return liveRequest;
    }

    // Hidden mobile Editor tab: keep settled iframes only while unchanged.
    if (
      frozenRequest &&
      liveRequest &&
      frozenRequest.revision === liveRequest.revision
    ) {
      return frozenRequest;
    }

    return null;
  }, [
    frozenRequest,
    liveRequest,
    previewEnabled,
    previewVisible,
  ]);

  const showPreviewDocument = previewRequest !== null;

  const handlePreviewInteract = useCallback(() => {
    onClosePopoversRef.current?.();
  }, []);
  const {
    iframeARef,
    iframeBRef,
    slotSrc,
    slotRevision,
    activeSlot,
    contentHeight,
    pageCount,
    ready,
    isUpdating: isPagedUpdating,
  } = usePagedPreviewIframe(previewRequest, handlePreviewInteract);
  const isPreviewUpdating = isMarkdownUpdating || isPagedUpdating;

  // Cache a settled request for mobile Editor-tab restore. Adjust during render
  // (React's "adjusting state when props change" pattern) instead of an effect
  // so we do not cascade a second commit after ready/visibility flips.
  if (!previewEnabled) {
    if (frozenRequest !== null) {
      setFrozenRequest(null);
    }
  } else if (
    previewVisible &&
    liveRequest &&
    (ready || Boolean(sampleSnapshot))
  ) {
    if (frozenRequest?.revision !== liveRequest.revision) {
      setFrozenRequest(liveRequest);
    }
  } else if (
    !previewVisible &&
    frozenRequest !== null &&
    (!liveRequest || frozenRequest.revision !== liveRequest.revision)
  ) {
    setFrozenRequest(null);
  }

  useEffect(() => {
    if (!previewVisible) {
      onUpdatingChange?.(false);
      return;
    }

    onUpdatingChange?.(isPreviewUpdating);
    return () => {
      onUpdatingChange?.(false);
    };
  }, [isPreviewUpdating, onUpdatingChange, previewVisible]);

  useEffect(() => {
    if (!onPageCountChange) {
      return;
    }

    // Sample snapshot page count is known from the manifest (and HTML module)
    // without mounting iframes — report it on mobile even before Preview opens.
    if (sampleSnapshot || sampleSnapshotMeta) {
      onPageCountChange(
        sampleSnapshot?.pageCount ?? sampleSnapshotMeta!.pageCount,
      );
      return;
    }

    // Keep the last page count while the mobile Editor tab hides the pane.
    if (!previewVisible) {
      return;
    }

    if (previewState.status === "empty" || !showPreviewDocument) {
      onPageCountChange(0);
      return;
    }

    if (ready) {
      onPageCountChange(pageCount);
    }
  }, [
    onPageCountChange,
    pageCount,
    previewState.status,
    previewVisible,
    ready,
    sampleSnapshot,
    sampleSnapshotMeta,
    showPreviewDocument,
  ]);

  // Host scale/geometry must track the visible document. While a buffer is
  // paginating, keep the last promoted page size — updating to Landscape
  // immediately was shrinking the still-visible portrait pages.
  const [frameRevision, setFrameRevision] = useState("");
  const [committedFrameOptions, setCommittedFrameOptions] =
    useState(pdfOptions);
  const pendingRevision = previewRequest?.revision ?? "";
  const activeShowsPending =
    Boolean(pendingRevision) && slotRevision[activeSlot] === pendingRevision;

  if (!pendingRevision && frameRevision !== "") {
    setFrameRevision("");
    setCommittedFrameOptions(pdfOptions);
  } else if (
    ready &&
    activeShowsPending &&
    frameRevision !== pendingRevision
  ) {
    setFrameRevision(pendingRevision);
    setCommittedFrameOptions(pdfOptions);
  }

  // Before the first promote (and in environments that never settle), follow
  // pending options. After ready, only follow them when the active slot has
  // already swapped to that document.
  const frameOptions =
    !ready || activeShowsPending ? pdfOptions : committedFrameOptions;
  const pageAspectRatio = getPdfPageAspectRatio(frameOptions);
  const pagePreviewWidth = getPdfPagePreviewWidth(frameOptions);
  const pagePreviewHeight = getPdfPagePreviewHeight(frameOptions);
  // Measure a full-width host; the scaled layout box is a child so ResizeObserver
  // still sees pane width changes after transform: scale shrinks the page stack.
  const { containerRef: measureRef, metrics: pageScaleMetrics } =
    usePreviewPageScale(pagePreviewWidth, showPreviewDocument);
  const pageScale = pageScaleMetrics?.scale ?? 1;
  const scaledPageWidthPx = pageScaleMetrics
    ? pageScaleMetrics.widthPx * pageScale
    : undefined;
  const scaledContentHeightPx =
    contentHeight !== null && pageScaleMetrics
      ? contentHeight * pageScale
      : undefined;
  const frameHeight = contentHeight ?? pagePreviewHeight;

  const showDocumentLoadingOverlay =
    showPreviewDocument && !ready && !isInitialRendering;
  const showPaneStatusOverlay =
    previewState.status === "empty" ||
    isInitialRendering ||
    previewState.status === "error" ||
    showDocumentLoadingOverlay;

  return (
    <section
      id="preview-pane"
      ref={setPreviewPaneRef}
      role="tabpanel"
      aria-labelledby="preview-tab"
      className={classNames(
        paneVisibility("preview", activeTab),
        "min-h-0 min-w-0 flex-col preview-surface",
      )}
    >
      {/* Outer clip hides native scrollbars that Cursor Simple Browser (and
          some Linux Chromium builds) still paint despite scrollbar-width: none. */}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        {showPaneStatusOverlay ? (
          <div
            data-testid="preview-status-overlay"
            className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center p-4"
            style={{ backgroundColor: previewSurfaceBackground }}
          >
            {previewState.status === "empty" ? <EmptyPreview /> : null}
            {isInitialRendering || showDocumentLoadingOverlay ? (
              <RenderingPreview pdfOptions={pdfOptions} />
            ) : null}
            {previewState.status === "error" ? (
              <PreviewError message={previewState.errorMessage} />
            ) : null}
          </div>
        ) : null}
        <div
          ref={scrollContainerRef}
          data-testid="preview-scroll-container"
          aria-label="PDF preview document"
          aria-live="polite"
          aria-busy={isPreviewUpdating || undefined}
          className="flex h-full min-h-0 justify-center overflow-y-auto overflow-x-hidden scrollbar-hidden p-4"
          style={{
            // Shift the native bar past the overflow:hidden parent so Cursor's
            // Simple Browser still hides it when scrollbar-width is ignored.
            width: "calc(100% + 2rem)",
            paddingRight: "calc(1rem + 2rem)",
          }}
        >
          {showPreviewDocument ? (
            <div
              ref={measureRef}
              className="w-full"
              data-testid="preview-page-measure"
            >
              <div
                className="relative mx-auto"
                data-testid="preview-page-stack"
                data-page-count={ready ? String(pageCount) : undefined}
                style={{
                  maxWidth: pagePreviewWidth,
                  width: scaledPageWidthPx ?? "100%",
                  height: scaledContentHeightPx,
                  aspectRatio: scaledContentHeightPx
                    ? undefined
                    : pageAspectRatio,
                }}
              >
                <div
                  data-testid="preview-page-frame"
                  className="relative origin-top-left"
                  style={{
                    width: pagePreviewWidth,
                    height: frameHeight,
                    transform:
                      pageScale === 1 ? undefined : `scale(${pageScale})`,
                  }}
                >
                  <div
                    data-testid="preview-page-content"
                    className="relative h-full w-full"
                  >
                    {(["a", "b"] as const).map((slot) => {
                      const isActive = ready && activeSlot === slot;
                      const iframeRef = slot === "a" ? iframeARef : iframeBRef;

                      return (
                        <iframe
                          key={slot}
                          ref={iframeRef}
                          title={
                            slot === activeSlot
                              ? "Rendered markdown preview"
                              : "Rendered markdown preview buffer"
                          }
                          aria-label={
                            slot === activeSlot
                              ? "Rendered markdown preview"
                              : undefined
                          }
                          aria-hidden={isActive ? undefined : true}
                          sandbox="allow-scripts allow-same-origin"
                          srcDoc={slotSrc[slot]}
                          className="block border-0 bg-transparent"
                          style={{
                            width: pagePreviewWidth,
                            height: frameHeight,
                            position: isActive ? "relative" : "absolute",
                            inset: isActive ? undefined : 0,
                            visibility: isActive ? "visible" : "hidden",
                            pointerEvents: isActive ? "auto" : "none",
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
